import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { ItOpsControllerService } from 'src/app/admin-api/api/itOpsController.service';
import { IntegrityCategory } from 'src/app/admin-api/model/integrityCategory';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-integrity-form',
  templateUrl: './integrity-form.component.html',
  styleUrls: ['./integrity-form.component.scss'],
  providers: [ConfirmationService, MessageService]
})
export class IntegrityFormComponent implements OnInit, OnChanges {
  @Input() integrityId?: number; // id quando o form é usado em modal
  @Input() embedded = false; // true quando está dentro do dialog
  @Output() saved = new EventEmitter<void>();
  @Output() saving = new EventEmitter<boolean>(); // emite true/false durante save

  form!: FormGroup;
  categories: IntegrityCategory[] = [];
  loading = false; // estado do botão Salvar
  pageLoading = false; // carregando dados (edição)
  isEdit = false;
  readonly resolutionOptions = [
    { label: 'Sem automacao', value: 'NONE' },
    { label: 'Script SQL', value: 'SQL_SCRIPT' },
    { label: 'Handler Java', value: 'JAVA_HANDLER' },
    { label: 'Handler Java (standalone)', value: 'JAVA_HANDLER_STANDALONE' }
  ];

  constructor(
    private fb: FormBuilder,
    private itOpsService: ItOpsControllerService,
    private router: Router,
    private route: ActivatedRoute,
    private confirm: ConfirmationService,
    private toast: MessageService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadCategories();

    // fallback se vier pela rota /it-ops/integrity/:id
    const idParam = this.route.snapshot.paramMap.get('id');
    if (!this.integrityId && idParam) {
      this.integrityId = Number(idParam);
    }

    this.isEdit = !!this.integrityId;
    if (this.isEdit) this.fetchIntegrity(this.integrityId!);

    // normaliza o valor digitado manualmente (# + uppercase) sempre
    this.form.get('integrityColor')!.valueChanges.subscribe((val) => {
      if (!val) return;
      const normalized = this.normalizeHex(val);
      if (normalized !== val) {
        this.form
          .get('integrityColor')!
          .setValue(normalized, { emitEvent: false });
      }
    });
  }

  private resetForm(): void {
    this.form.reset({
      integrityName: '',
      integrityViewName: '',
      integrityDescription: '',
      integrityColor: '',
      integrityCategoryId: null,
      integrityQuery: '',
      resolutionType: 'NONE',
      resolutionQuery: '',
      automaticUpdate: false,
      enableToViews: false
    });
    this.updateResolutionValidators();
  }

  private updateResolutionValidators(): void {
    const resolutionControl = this.form?.get('resolutionQuery');
    const integrityQueryControl = this.form?.get('integrityQuery');
    if (!resolutionControl || !integrityQueryControl) return;
    const resolutionType = this.form.get('resolutionType')?.value;
    if (resolutionType === 'SQL_SCRIPT') {
      resolutionControl.setValidators([Validators.required]);
    } else {
      resolutionControl.clearValidators();
    }
    if (resolutionType === 'JAVA_HANDLER_STANDALONE') {
      integrityQueryControl.clearValidators();
    } else {
      integrityQueryControl.setValidators([Validators.required]);
    }
    resolutionControl.updateValueAndValidity({ emitEvent: false });
    integrityQueryControl.updateValueAndValidity({ emitEvent: false });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // quando abrir o modal para outro card
    if (changes['integrityId'] && !changes['integrityId'].firstChange) {
      this.isEdit = !!this.integrityId;
      if (this.isEdit) {
        this.fetchIntegrity(this.integrityId!);
      } else {
        this.resetForm();
      }
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      integrityName: ['', [Validators.required, Validators.maxLength(50)]],
      integrityViewName: ['', [Validators.required, Validators.maxLength(255)]],
      integrityDescription: ['', Validators.maxLength(255)],
      // ✅ valida #RGB ou #RRGGBB
      integrityColor: [
        '',
        [
          Validators.maxLength(10),
          Validators.pattern(/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/)
        ]
      ],
      integrityCategoryId: [null, Validators.required],
      integrityQuery: [''],
      resolutionType: ['NONE', Validators.required],
      resolutionQuery: [''],
      automaticUpdate: [false],
      enableToViews: [false]
    });

    this.form
      .get('resolutionType')!
      .valueChanges.subscribe(() => this.updateResolutionValidators());
    this.updateResolutionValidators();
  }

  private async loadCategories() {
    try {
      const res = await lastValueFrom(this.itOpsService.getAllCategories());
      this.categories = res || [];
    } catch (err) {
      console.error('Erro ao carregar categorias', err);
    }
  }

  private async fetchIntegrity(id: number) {
    try {
      this.pageLoading = true;
      LoaderService.showLoader();
      const res = await lastValueFrom(this.itOpsService.getIntegrity(id));
      this.form.patchValue({
        integrityName: res.integrityName,
        integrityViewName: res.integrityViewName,
        integrityDescription: res.integrityDescription,
        integrityColor: res.integrityColor,
        integrityCategoryId: res.integrityCategoryId,
        integrityQuery: res.integrityQuery,
        resolutionType: res.resolutionType ?? 'NONE',
        resolutionQuery: res.resolutionQuery ?? '',
        automaticUpdate: Boolean(res.automaticUpdate),
        enableToViews: Boolean(res.enableToViews)
      });
      this.updateResolutionValidators();
    } catch (err) {
      AppDialogService.showErrorDialog(err);
      console.error(err);
    } finally {
      this.pageLoading = false;
      LoaderService.showLoader(false);
    }
  }

  private normalizeHex(val: string): string {
    const v = (val || '').trim();
    if (!v) return v;
    const raw = v.startsWith('#') ? v.slice(1) : v;
    if (/^[0-9a-fA-F]{3}$/.test(raw) || /^[0-9a-fA-F]{6}$/.test(raw)) {
      return ('#' + raw).toUpperCase();
    }
    return v.toUpperCase();
  }

  private normalizeResolutionQuery(sql?: string): string | null {
    if (!sql) return null;
    const cleaned = sql
      .replace(/\/\*[\s\S]*?\*\//g, ' ')
      .replace(/--.*$/gm, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned || null;
  }

  private to6HexNoHash(val?: string): string {
    const raw = (val || '').replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(raw)) {
      return raw
        .split('')
        .map((c) => c + c)
        .join('')
        .toUpperCase();
    }
    if (/^[0-9a-fA-F]{6}$/.test(raw)) {
      return raw.toUpperCase();
    }
    return '';
  }

  get pickerValue(): string {
    return this.to6HexNoHash(this.form.get('integrityColor')?.value) || '';
  }

  onPickerChange(hexValue: string): void {
    const sanitized = (hexValue || '').replace(/^#/, '');
    const withHash = this.normalizeHex('#' + sanitized);
    this.form.get('integrityColor')!.setValue(withHash);
    this.form.get('integrityColor')!.markAsDirty();
    this.form.get('integrityColor')!.markAsTouched();
  }

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    try {
      this.loading = true;
      this.saving.emit(true);
      LoaderService.showLoader();

      const formValue = this.form.value as any;
      const body = {
        ...formValue,
        integrityQuery: (formValue.integrityQuery ?? '').trim(),
        resolutionType: (formValue.resolutionType ?? 'NONE').toUpperCase(),
        resolutionQuery:
          (formValue.resolutionType ?? 'NONE').toUpperCase() === 'SQL_SCRIPT'
            ? this.normalizeResolutionQuery(formValue.resolutionQuery)
            : null,
        automaticUpdate: Boolean(formValue.automaticUpdate),
        enableToViews: Boolean(formValue.enableToViews)
      };
      const req$ = this.isEdit
        ? this.itOpsService.updateIntegrity(body, this.integrityId!)
        : this.itOpsService.createIntegrity(body);

      await lastValueFrom(req$);

      this.toast.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: this.isEdit ? 'Integridade atualizada.' : 'Integridade criada.'
      });

      if (this.embedded) {
        this.saved.emit();
      } else {
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => this.router.navigate(['/it-ops/integrity']));
      }
    } catch (err) {
      AppDialogService.showErrorDialog(err);
      console.error('Erro ao salvar integridade', err);
    } finally {
      this.loading = false;
      this.saving.emit(false);
      LoaderService.showLoader(false);
    }
  }
}